import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, ShieldCheck, ShieldAlert, AlertTriangle, Globe } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_CONFIG = {
  severe: { bg: 'bg-red-500/15', border: 'border-red-500/40', text: 'text-red-400', label: 'Severe' },
  moderate: { bg: 'bg-yellow-500/15', border: 'border-yellow-500/40', text: 'text-yellow-400', label: 'Moderate' },
  mild: { bg: 'bg-orange-500/15', border: 'border-orange-500/40', text: 'text-orange-400', label: 'Mild' },
};

const DrugInteractionDialog = ({ open, onOpenChange, cartItems }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [lang, setLang] = useState('en');

  const handleCheck = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const names = cartItems.map(i => i.item_name || i.name).filter(Boolean);
      const res = await axios.post(`${API}/pos/check-interactions`, { item_names: names });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Interaction check failed');
    } finally {
      setLoading(false);
    }
  };

  const summaryConfig = {
    safe: { icon: ShieldCheck, color: 'text-green-400', bg: 'bg-green-500/10', label: 'No Interactions Found', labelAr: 'لا توجد تفاعلات' },
    warning: { icon: AlertTriangle, color: 'text-yellow-400', bg: 'bg-yellow-500/10', label: 'Interactions Detected', labelAr: 'تم اكتشاف تفاعلات' },
    danger: { icon: ShieldAlert, color: 'text-red-400', bg: 'bg-red-500/10', label: 'Serious Interactions!', labelAr: '!تفاعلات خطيرة' },
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setResult(null); setError(null); } onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto" data-testid="drug-interaction-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-cyan-400" />
            Medication Interaction Check
          </DialogTitle>
        </DialogHeader>

        {/* Items being checked */}
        <div className="p-3 bg-muted/20 rounded-lg border border-border/50">
          <div className="text-xs text-muted-foreground mb-1.5 font-medium">Cart Items ({cartItems.length})</div>
          <div className="flex flex-wrap gap-1.5">
            {cartItems.map((item, i) => (
              <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary border border-primary/20">
                {item.item_name || item.name}
              </span>
            ))}
          </div>
        </div>

        {/* Initial state — check button */}
        {!result && !loading && !error && (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <ShieldCheck className="w-14 h-14 text-cyan-400/40" />
            <p className="text-muted-foreground text-center text-sm">
              Check for potential drug-drug interactions<br />before dispensing to the patient
            </p>
            <Button onClick={handleCheck} className="gap-2 h-11 px-6" data-testid="check-interactions-btn">
              <ShieldCheck className="w-4 h-4" /> Check Interactions
            </Button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
            <p className="text-muted-foreground text-sm">Analyzing interactions with Claude Opus 4.6...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="py-6 text-center">
            <p className="text-red-400 text-sm mb-4">{error}</p>
            <Button variant="outline" onClick={handleCheck}>Retry</Button>
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="space-y-4">
            {/* Summary banner */}
            {(() => {
              const cfg = summaryConfig[result.summary] || summaryConfig.safe;
              const Icon = cfg.icon;
              return (
                <div className={`flex items-center gap-3 p-4 rounded-lg ${cfg.bg}`}>
                  <Icon className={`w-8 h-8 ${cfg.color}`} />
                  <div>
                    <div className={`font-bold text-lg ${cfg.color}`}>{cfg.label}</div>
                    <div className={`text-sm ${cfg.color} opacity-80`} dir="rtl">{cfg.labelAr}</div>
                  </div>
                </div>
              );
            })()}

            {/* Safe message when no interactions */}
            {result.summary === 'safe' && (result.safe_message_en || result.safe_message_ar) && (
              <div className="p-4 text-center">
                <p className="text-green-400 text-sm">{result.safe_message_en}</p>
                {result.safe_message_ar && <p className="text-green-400 text-sm mt-1" dir="rtl">{result.safe_message_ar}</p>}
              </div>
            )}

            {/* Interaction list */}
            {result.interactions && result.interactions.length > 0 && (
              <div className="space-y-3">
                {/* Language toggle */}
                <div className="flex gap-1 justify-end">
                  <button
                    onClick={() => setLang('en')}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${lang === 'en' ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                    data-testid="interaction-lang-en"
                  >
                    <Globe className="w-3 h-3 inline mr-1" />English
                  </button>
                  <button
                    onClick={() => setLang('ar')}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${lang === 'ar' ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                    data-testid="interaction-lang-ar"
                  >
                    <Globe className="w-3 h-3 inline mr-1" />العربية
                  </button>
                </div>

                {result.interactions.map((ix, idx) => {
                  const sev = SEVERITY_CONFIG[ix.severity] || SEVERITY_CONFIG.mild;
                  const langData = ix[lang] || ix.en || {};
                  return (
                    <div key={idx} className={`rounded-lg border ${sev.border} ${sev.bg} p-4`} dir={lang === 'ar' ? 'rtl' : 'ltr'} data-testid={`interaction-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className={`w-4 h-4 ${sev.text}`} />
                          <span className="font-bold text-sm">{ix.drug_a}</span>
                          <span className="text-muted-foreground text-xs">+</span>
                          <span className="font-bold text-sm">{ix.drug_b}</span>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${sev.bg} ${sev.text} border ${sev.border}`}>
                          {sev.label}
                        </span>
                      </div>
                      {langData.effect && (
                        <div className="text-sm mb-1.5">
                          <span className="text-muted-foreground font-medium">{lang === 'ar' ? 'التأثير: ' : 'Effect: '}</span>
                          {langData.effect}
                        </div>
                      )}
                      {langData.recommendation && (
                        <div className="text-sm">
                          <span className="text-muted-foreground font-medium">{lang === 'ar' ? 'التوصية: ' : 'Recommendation: '}</span>
                          {langData.recommendation}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Re-check button */}
            <div className="flex justify-center pt-2">
              <Button variant="ghost" size="sm" className="gap-1.5 text-cyan-400" onClick={handleCheck} disabled={loading}>
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                Re-check
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DrugInteractionDialog;
